import { Dialog, Transition } from '@headlessui/react'
import {
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { API, Auth } from 'aws-amplify'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import DropZone from '../../../components/DropZone'
import { useAppContext } from '../../../libs/context'
import BotPreview from './BotPreview'

// Helper to generate unique IDs
const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

const ProviderConfiguration = ({
  userInfo,
  refreshUserInfo,
  setLoading,
  setOpenResult
}) => {
  const { userInfo: contextUserInfo } = useAppContext()
  const isAdmin = contextUserInfo?.isAdmin || false

  // Modal state for delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [botToDelete, setBotToDelete] = useState(null)
  // Use botData field - if it's an array, use it; if it's the old format, convert it
  const rawBotData = userInfo?.botData
  let currentBots = []

  if (Array.isArray(rawBotData)) {
    // Already in new format (array)
    currentBots = rawBotData
  } else if (rawBotData && typeof rawBotData === 'object') {
    // Old format - convert to array format
    // Check if we have rcsProviders to determine provider
    const providers = userInfo?.rcsProviders || {}
    const supplierRcs = userInfo?.supplierRcs || ''

    let provider = ''
    if (supplierRcs === 'orange' || providers.orange?.enabled) {
      provider = 'orange'
    } else if (supplierRcs === 'google' || providers.google?.enabled) {
      provider = 'google'
    }

    if (provider) {
      // Migrate old format to new array format
      currentBots = [
        {
          id: generateId(),
          provider: provider,
          name: rawBotData.name || providers[provider]?.botName || 'Bot',
          botData: {
            description: rawBotData.description,
            profilePicture: rawBotData.profilePicture,
            bannerPicture: rawBotData.bannerPicture
          },
          orangeConfig:
            provider === 'orange'
              ? {
                  botName: providers.orange?.botName || rawBotData.name,
                  botId: providers.orange?.botId
                }
              : undefined,
          googleConfig:
            provider === 'google'
              ? {
                  botName: providers.google?.botName || rawBotData.name,
                  projectId: providers.google?.projectId,
                  serviceAccountEmail: providers.google?.serviceAccountEmail
                }
              : undefined
        }
      ]
    }
  }

  // Selected bot index for editing
  const [selectedBotIndex, setSelectedBotIndex] = useState(null)
  // Temporary bots being created (not yet saved)
  const [tempBots, setTempBots] = useState([])

  // Combine saved bots with temporary bots for display
  const allBots = [...currentBots, ...tempBots]

  // Initialize with first bot if exists
  useEffect(() => {
    if (allBots.length > 0 && selectedBotIndex === null) {
      setSelectedBotIndex(0)
    }
  }, [allBots.length])

  // Get current bot being edited
  const currentBot =
    selectedBotIndex !== null && allBots[selectedBotIndex]
      ? allBots[selectedBotIndex]
      : null

  // Get default provider from user settings
  const getDefaultProvider = () => {
    const supplierRcs = userInfo?.supplierRcs || ''
    const providers = userInfo?.rcsProviders || {}
    if (supplierRcs === 'orange' || providers.orange?.enabled) return 'orange'
    if (supplierRcs === 'google' || providers.google?.enabled) return 'google'
    return 'orange' // Default to orange if nothing configured
  }

  const defaultProvider = getDefaultProvider()

  // Bot form state - initialize from current bot or defaults
  const [botProvider, setBotProvider] = useState(
    currentBot?.provider || defaultProvider
  )
  const [botName, setBotName] = useState(currentBot?.name || '')
  const [botDescription, setBotDescription] = useState(
    currentBot?.botData?.description || ''
  )
  const [profilePicture, setProfilePicture] = useState(
    currentBot?.botData?.profilePicture || null
  )
  const [bannerPicture, setBannerPicture] = useState(
    currentBot?.botData?.bannerPicture || null
  )

  // Orange specific fields
  const [orangeBotName, setOrangeBotName] = useState(
    currentBot?.orangeConfig?.botName ||
      userInfo?.rcsProviders?.orange?.botName ||
      ''
  )
  const [orangeBotId, setOrangeBotId] = useState(
    currentBot?.orangeConfig?.botId ||
      userInfo?.rcsProviders?.orange?.botId ||
      ''
  )

  // Google specific fields
  const [googleBotName, setGoogleBotName] = useState(
    currentBot?.googleConfig?.botName ||
      userInfo?.rcsProviders?.google?.botName ||
      ''
  )
  const [googleProjectId, setGoogleProjectId] = useState(
    currentBot?.googleConfig?.projectId ||
      userInfo?.rcsProviders?.google?.projectId ||
      ''
  )
  const [googleServiceAccountEmail, setGoogleServiceAccountEmail] = useState(
    currentBot?.googleConfig?.serviceAccountEmail ||
      userInfo?.rcsProviders?.google?.serviceAccountEmail ||
      ''
  )

  // Track if we should skip syncing provider (when user manually changes it)
  const providerChangeRef = useRef(false)

  // Track if we're adding a new bot to prevent useEffect from overriding reset
  const addingNewBotRef = useRef(false)

  // Sync form when selected bot changes
  useEffect(() => {
    // If we just added a new bot, skip syncing (form was already reset)
    if (addingNewBotRef.current) {
      addingNewBotRef.current = false
      return
    }

    // Skip provider sync if user just changed it manually
    if (providerChangeRef.current) {
      providerChangeRef.current = false
      // Still sync other fields, just not provider
      if (currentBot) {
        setBotName(currentBot.name || '')
        setBotDescription(currentBot.botData?.description || '')
        setProfilePicture(currentBot.botData?.profilePicture || null)
        setBannerPicture(currentBot.botData?.bannerPicture || null)
        setOrangeBotName(
          currentBot.orangeConfig?.botName ||
            userInfo?.rcsProviders?.orange?.botName ||
            ''
        )
        setOrangeBotId(
          currentBot.orangeConfig?.botId ||
            userInfo?.rcsProviders?.orange?.botId ||
            ''
        )
        setGoogleBotName(
          currentBot.googleConfig?.botName ||
            userInfo?.rcsProviders?.google?.botName ||
            ''
        )
        setGoogleProjectId(
          currentBot.googleConfig?.projectId ||
            userInfo?.rcsProviders?.google?.projectId ||
            ''
        )
        setGoogleServiceAccountEmail(
          currentBot.googleConfig?.serviceAccountEmail ||
            userInfo?.rcsProviders?.google?.serviceAccountEmail ||
            ''
        )
      }
      return
    }

    if (currentBot && !currentBot.isTemporary) {
      // Editing existing saved bot
      setBotProvider(currentBot.provider || defaultProvider)
      setBotName(currentBot.name || '')
      setBotDescription(currentBot.botData?.description || '')
      setProfilePicture(currentBot.botData?.profilePicture || null)
      setBannerPicture(currentBot.botData?.bannerPicture || null)
      setOrangeBotName(
        currentBot.orangeConfig?.botName ||
          userInfo?.rcsProviders?.orange?.botName ||
          ''
      )
      setOrangeBotId(
        currentBot.orangeConfig?.botId ||
          userInfo?.rcsProviders?.orange?.botId ||
          ''
      )
      setGoogleBotName(
        currentBot.googleConfig?.botName ||
          userInfo?.rcsProviders?.google?.botName ||
          ''
      )
      setGoogleProjectId(
        currentBot.googleConfig?.projectId ||
          userInfo?.rcsProviders?.google?.projectId ||
          ''
      )
      setGoogleServiceAccountEmail(
        currentBot.googleConfig?.serviceAccountEmail ||
          userInfo?.rcsProviders?.google?.serviceAccountEmail ||
          ''
      )
    } else if (currentBot?.isTemporary) {
      // Temporary bot - only sync if it has meaningful data (not just defaults)
      // Check if bot has actual user-entered data
      const hasUserData =
        currentBot.name ||
        (currentBot.orangeConfig &&
          (currentBot.orangeConfig.botName || currentBot.orangeConfig.botId)) ||
        (currentBot.googleConfig &&
          (currentBot.googleConfig.botName ||
            currentBot.googleConfig.projectId ||
            currentBot.googleConfig.serviceAccountEmail))

      if (hasUserData) {
        // Bot has user data, sync it
        setBotProvider(currentBot.provider || defaultProvider)
        setBotName(currentBot.name || '')
        setBotDescription(currentBot.botData?.description || '')
        setProfilePicture(currentBot.botData?.profilePicture || null)
        setBannerPicture(currentBot.botData?.bannerPicture || null)
        setOrangeBotName(currentBot.orangeConfig?.botName || '')
        setOrangeBotId(currentBot.orangeConfig?.botId || '')
        setGoogleBotName(currentBot.googleConfig?.botName || '')
        setGoogleProjectId(currentBot.googleConfig?.projectId || '')
        setGoogleServiceAccountEmail(
          currentBot.googleConfig?.serviceAccountEmail || ''
        )
      } else {
        // Bot is empty/new - only set provider, keep form empty
        setBotProvider(defaultProvider)
        // Don't set other fields - keep them as they were reset in handleAddBot
      }
    }
  }, [selectedBotIndex, allBots.length])

  // Update temporary bot name in list when name changes (debounced)
  useEffect(() => {
    if (currentBot?.isTemporary && selectedBotIndex !== null && botName) {
      const tempIndex = selectedBotIndex - currentBots.length
      if (tempIndex >= 0 && tempIndex < tempBots.length) {
        const updatedTempBots = [...tempBots]
        if (updatedTempBots[tempIndex].name !== botName) {
          updatedTempBots[tempIndex] = {
            ...updatedTempBots[tempIndex],
            name: botName
          }
          setTempBots(updatedTempBots)
        }
      }
    }
  }, [botName, selectedBotIndex])

  const handleProfileDrop = (files) => {
    setProfilePicture(files[0])
  }

  const handleBannerDrop = (files) => {
    setBannerPicture(files[0])
  }

  const uploadImage = async (file, key, tokenReq) => {
    if (!file) return null
    if (typeof file === 'string') {
      return file
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('key', key)
    const result = await API.post('rcs-api', '/upload-image', {
      headers: {
        Authorization: tokenReq
      },
      body: formData
    })
    return result.location
  }

  const handleAddBot = () => {
    // Set flag to prevent useEffect from overriding the reset
    addingNewBotRef.current = true

    // Create a new temporary bot first (empty)
    const newTempBot = {
      id: `temp-${Date.now()}`,
      provider: defaultProvider,
      name: '',
      botData: {},
      isTemporary: true
    }

    const newTempBots = [...tempBots, newTempBot]
    setTempBots(newTempBots)

    // Select the newly created bot (it will be at the end of allBots)
    const newIndex = currentBots.length + newTempBots.length - 1

    // Reset the form completely AFTER creating the bot
    setBotProvider(defaultProvider)
    setBotName('')
    setBotDescription('')
    setProfilePicture(null)
    setBannerPicture(null)
    // Don't pre-fill with userInfo values - keep empty for new bot
    setOrangeBotName('')
    setOrangeBotId('')
    setGoogleBotName('')
    setGoogleProjectId('')
    setGoogleServiceAccountEmail('')

    // Set selected index after resetting form
    setSelectedBotIndex(newIndex)

    // Reset provider change flag
    providerChangeRef.current = false
  }

  const handleDeleteClick = (botIndex, e) => {
    e.stopPropagation()
    const bot = allBots[botIndex]
    setBotToDelete({ index: botIndex, bot })
    setDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!botToDelete) return

    const { index: botIndex, bot } = botToDelete

    // If it's a temporary bot, just remove it from tempBots
    if (bot?.isTemporary) {
      const updatedTempBots = tempBots.filter((_, idx) => {
        const tempIndex = currentBots.length + idx
        return tempIndex !== botIndex
      })
      setTempBots(updatedTempBots)

      // Adjust selected index
      if (botIndex < selectedBotIndex) {
        setSelectedBotIndex(selectedBotIndex - 1)
      } else if (botIndex === selectedBotIndex) {
        const newAllBots = [...currentBots, ...updatedTempBots]
        setSelectedBotIndex(
          newAllBots.length > 0
            ? Math.min(selectedBotIndex, newAllBots.length - 1)
            : null
        )
      }
      setDeleteModalOpen(false)
      setBotToDelete(null)
      return
    }

    // If it's a saved bot, delete from server
    const updatedBots = currentBots.filter((_, index) => index !== botIndex)

    setLoading(true)
    setOpenResult(true)
    setDeleteModalOpen(false)

    try {
      // Use apiPost helper which handles impersonation automatically
      const { apiPost } = await import('../../../libs/apiHelper')
      await apiPost('/users', {
        body: {
          [userInfo.userId]: {
            botData: updatedBots // Save as botData (array)
          }
        }
      })

      // Adjust selected index
      if (botIndex < selectedBotIndex) {
        setSelectedBotIndex(selectedBotIndex - 1)
      } else if (botIndex === selectedBotIndex) {
        const newAllBots = [...updatedBots, ...tempBots]
        setSelectedBotIndex(
          newAllBots.length > 0
            ? Math.min(selectedBotIndex, newAllBots.length - 1)
            : null
        )
      }

      refreshUserInfo()
    } catch (err) {
      console.error(err)
      alert('Error al eliminar el bot. Por favor, inténtalo de nuevo.')
    }

    setLoading(false)
    setBotToDelete(null)
  }

  // Check if bot can be saved (minimum requirements met)
  const canSaveBot = () => {
    const finalProvider = botProvider || defaultProvider
    if (!finalProvider) return false

    if (finalProvider === 'orange') {
      return !!(orangeBotName && orangeBotId)
    }

    if (finalProvider === 'google') {
      return !!(googleBotName && googleProjectId && googleServiceAccountEmail)
    }

    return false
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!canSaveBot()) {
      alert(
        'Por favor completa al menos la información del proveedor seleccionado'
      )
      return
    }

    // Use default provider if not selected
    const finalProvider = botProvider || defaultProvider

    setLoading(true)
    setOpenResult(true)

    try {
      const tokenReq = (await Auth.currentAuthenticatedUser()).signInUserSession
        .idToken.jwtToken

      // Upload images
      let profileUrl = null
      let bannerUrl = null
      if (profilePicture || bannerPicture) {
        const results = await Promise.all([
          uploadImage(profilePicture, 'profile', tokenReq),
          uploadImage(bannerPicture, 'banner', tokenReq)
        ])
        profileUrl = results[0]
        bannerUrl = results[1]
      }

      const botData =
        profileUrl || bannerUrl || botDescription
          ? {
              description: botDescription,
              profilePicture: profileUrl,
              bannerPicture: bannerUrl
            }
          : {}

      // Build bot configuration - use default provider if not specified
      const finalProvider = botProvider || defaultProvider
      const botConfig = {
        id: currentBot?.id || generateId(),
        provider: finalProvider,
        name: finalProvider === 'orange' ? orangeBotName : googleBotName,
        botData: botData,
        orangeConfig:
          finalProvider === 'orange'
            ? {
                botName: orangeBotName,
                botId: orangeBotId
              }
            : undefined,
        googleConfig:
          finalProvider === 'google'
            ? {
                botName: googleBotName,
                projectId: googleProjectId,
                serviceAccountEmail: googleServiceAccountEmail
              }
            : undefined
      }

      // Update botProvider state to match finalProvider
      if (!botProvider) {
        setBotProvider(finalProvider)
      }

      // Update or add bot
      let updatedBots = [...currentBots]
      const isTemporaryBot = currentBot?.isTemporary

      if (
        selectedBotIndex !== null &&
        selectedBotIndex < currentBots.length &&
        !isTemporaryBot
      ) {
        // Update existing saved bot
        updatedBots[selectedBotIndex] = botConfig
      } else {
        // Add new bot (either from temporary or new)
        updatedBots.push(botConfig)
        // Remove from tempBots if it was temporary
        if (isTemporaryBot) {
          const updatedTempBots = tempBots.filter(
            (bot) => bot.id !== currentBot.id
          )
          setTempBots(updatedTempBots)
          // Update selected index to point to the newly saved bot
          setSelectedBotIndex(updatedBots.length - 1)
        } else {
          setSelectedBotIndex(updatedBots.length - 1)
        }
      }

      // Use apiPost helper which handles impersonation automatically
      const { apiPost } = await import('../../../libs/apiHelper')
      await apiPost('/users', {
        body: {
          [userInfo.userId]: {
            botData: updatedBots // Save as botData (array)
          }
        }
      })

      refreshUserInfo()
    } catch (err) {
      console.error(err)
      alert('Error al guardar la configuración. Por favor, inténtalo de nuevo.')
    }

    setLoading(false)
  }

  // Use botProvider state directly (it should always be set to defaultProvider or user selection)
  const isOrangeSelected = botProvider === 'orange'
  const isGoogleSelected = botProvider === 'google'

  return (
    <div className="mt-6">
      <div className="flex gap-6">
        {/* Sidebar with bot list */}
        <div className="w-48 flex-shrink-0">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">
                Bots Configurados
              </h3>
              <button
                type="button"
                onClick={handleAddBot}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded"
              >
                <PlusIcon className="w-4 h-4" />
                Añadir
              </button>
            </div>

            <div className="space-y-2">
              {allBots.map((bot, index) => (
                <div
                  key={bot.id || index}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedBotIndex === index
                      ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedBotIndex(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="mb-1">
                        <span className="text-xs font-medium text-gray-900 truncate block">
                          {bot.name ||
                            (bot.isTemporary
                              ? 'Nuevo bot'
                              : `Bot ${index + 1}`)}
                        </span>
                        {bot.isTemporary && (
                          <span className="text-xs text-gray-400 italic">
                            (sin guardar)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {bot.botData?.description || 'Sin descripción'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteClick(index, e)}
                      className="ml-2 text-gray-400 hover:text-red-600 flex-shrink-0"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {allBots.length === 0 && (
                <div className="text-center py-4 text-sm text-gray-500">
                  No hay bots configurados
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main form */}
        <div className="flex-1">
          {selectedBotIndex !== null && currentBot ? (
            <form onSubmit={handleSubmit}>
              {/* Provider Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Orange Provider Card */}
                <div
                  className={`relative rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    isOrangeSelected
                      ? 'border-orange-400 bg-orange-50/50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-sm'
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    providerChangeRef.current = true
                    setBotProvider('orange')
                  }}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                            isOrangeSelected ? 'bg-orange-500' : 'bg-gray-200'
                          }`}
                        >
                          <span className="text-2xl">🍊</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Orange
                          </h3>
                          <p className="text-sm text-gray-500">
                            RCS via Alaris
                          </p>
                        </div>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isOrangeSelected
                            ? 'border-orange-500 bg-orange-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isOrangeSelected && (
                          <svg
                            className="w-4 h-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </div>

                    {isOrangeSelected && (
                      <div
                        className="space-y-4 pt-4 border-t border-orange-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bot Name
                          </label>
                          <input
                            type="text"
                            value={orangeBotName}
                            onChange={(e) => setOrangeBotName(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                            placeholder="tadoo_multicanal_testing"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bot ID
                          </label>
                          <input
                            type="text"
                            value={orangeBotId}
                            onChange={(e) => setOrangeBotId(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                            placeholder="GZMiksX3mnbJtAte"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {isOrangeSelected && (
                    <div className="absolute -top-2 -right-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white shadow-sm">
                        Active
                      </span>
                    </div>
                  )}
                </div>

                {/* Google Provider Card */}
                <div
                  className={`relative rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    isGoogleSelected
                      ? 'border-blue-400 bg-blue-50/50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    providerChangeRef.current = true
                    setBotProvider('google')
                  }}
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                            isGoogleSelected ? 'bg-blue-500' : 'bg-gray-200'
                          }`}
                        >
                          <span className="text-2xl">🔵</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Google
                          </h3>
                          <p className="text-sm text-gray-500">RBM Platform</p>
                        </div>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isGoogleSelected
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isGoogleSelected && (
                          <svg
                            className="w-4 h-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </div>

                    {isGoogleSelected && (
                      <div
                        className="space-y-4 pt-4 border-t border-blue-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bot Name
                          </label>
                          <input
                            type="text"
                            value={googleBotName}
                            onChange={(e) => setGoogleBotName(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="My RBM Bot"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Project ID
                          </label>
                          <input
                            type="text"
                            value={googleProjectId}
                            onChange={(e) => setGoogleProjectId(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="rbm-project-id"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Account Email
                          </label>
                          <input
                            type="email"
                            value={googleServiceAccountEmail}
                            onChange={(e) =>
                              setGoogleServiceAccountEmail(e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            placeholder="rbm-agent@project.iam.gserviceaccount.com"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {isGoogleSelected && (
                    <div className="absolute -top-2 -right-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500 text-white shadow-sm">
                        Active
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bot Appearance */}
              <div className="relative rounded-xl border-2 border-gray-200 bg-white mb-6">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center bg-indigo-500">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Bot Appearance
                      </h3>
                      <p className="text-sm text-gray-500">
                        Configuración visual del bot
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bot Description
                        </label>
                        <textarea
                          value={botDescription}
                          onChange={(e) => setBotDescription(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none"
                          rows={3}
                          placeholder="Describe your bot..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Profile Picture
                          <span className="text-gray-400 font-normal ml-1">
                            (224×224px)
                          </span>
                        </label>
                        <DropZone
                          image={
                            typeof profilePicture === 'string'
                              ? {
                                  preview: profilePicture,
                                  name: 'Current image'
                                }
                              : profilePicture
                          }
                          onDrop={handleProfileDrop}
                          requiredSize={{ width: 224, height: 224 }}
                          onRemoveImage={() => setProfilePicture(null)}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Banner Picture
                          <span className="text-gray-400 font-normal ml-1">
                            (1440×448px)
                          </span>
                        </label>
                        <DropZone
                          image={
                            typeof bannerPicture === 'string'
                              ? {
                                  preview: bannerPicture,
                                  name: 'Current image'
                                }
                              : bannerPicture
                          }
                          onDrop={handleBannerDrop}
                          requiredSize={{ width: 1440, height: 448 }}
                          onRemoveImage={() => setBannerPicture(null)}
                        />
                      </div>
                    </div>

                    <div className="flex justify-center lg:justify-end">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-3 text-center lg:text-right">
                          Preview
                        </p>
                        <BotPreview
                          name={
                            isOrangeSelected
                              ? orangeBotName
                              : isGoogleSelected
                              ? googleBotName
                              : 'Bot Name'
                          }
                          description={botDescription}
                          profilePicture={profilePicture}
                          bannerPicture={bannerPicture}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!canSaveBot()}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    canSaveBot()
                      ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {currentBot && !currentBot.isTemporary
                    ? 'Actualizar Bot'
                    : 'Guardar Bot'}
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>Selecciona un bot de la lista para configurarlo</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={deleteModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDeleteModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Eliminar bot
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          ¿Estás seguro de que quieres eliminar el bot{' '}
                          <span className="font-semibold text-gray-900">
                            {botToDelete?.bot?.name || 'sin nombre'}
                          </span>
                          ?
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Esta acción no se puede deshacer.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={handleConfirmDelete}
                    >
                      Eliminar
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setDeleteModalOpen(false)
                        setBotToDelete(null)
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  )
}

export default ProviderConfiguration
